import { Alert, Button, PageHeader, Skeleton, StatusBadge, useToast } from '@catering/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useState, type JSX } from 'react';

import { loadDelivery } from '../../deliveries/deliveries-repository';
import { QueueItemStatus } from '../../deliveries/queue-status';
import { useSignatureQueue } from '../../deliveries/use-signature-queue';
import { formatClock, formatSiteAddress } from '../../deliveries/delivery-format';
import { SignOnScreenModal } from '../delivery-notes/components/sign-on-screen-modal';

const myDeliveryRoute = getRouteApi('/protected/entregas/$id');

const SIGNABLE_STATUSES = ['DISPATCHED', 'DELIVERED'];

/** Phone-friendly detail of one note: items and a big "Firmar entrega" button into the existing signature modal. */
export function MyDeliveryDetailPage(): JSX.Element {
  const { id } = myDeliveryRoute.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [signing, setSigning] = useState(false);

  const queue = useSignatureQueue();
  const query = useQuery({ queryKey: ['deliveries', 'detail', id], queryFn: () => loadDelivery(id) });

  if (query.isPending) {
    return <Skeleton className="h-48 w-full" />;
  }

  if (query.isError) {
    return <Alert tone="danger">No pudimos cargar el remito. Intentá de nuevo.</Alert>;
  }

  const note = query.data.data;
  const address = formatSiteAddress(note);
  const queued = queue.byNote.get(note.id);

  return (
    <div className="flex flex-col gap-4">
      <Link to="/entregas" className="text-sm text-slate-600 hover:underline">
        ← Mis entregas
      </Link>
      <PageHeader title={note.noteNumber} />
      <div className="flex flex-col gap-1">
        <StatusBadge domain="deliverynote" status={note.status} />
        <span className="font-medium text-slate-900">{note.customerNameSnapshot}</span>
        <span className="text-slate-700">{note.siteNameSnapshot}</span>
        {address !== null ? <span className="text-sm text-slate-600">{address}</span> : null}
        <span className="text-sm text-slate-600">
          {query.data.fromCache
            ? `Sin conexión — mostrando lo último que cargaste a las ${formatClock(query.data.fetchedAt)}`
            : `Datos actualizados a las ${formatClock(query.data.fetchedAt)}`}
        </span>
        {queued !== undefined ? (
          <QueueItemStatus item={queued} onDismiss={(noteId) => void queue.dismiss(noteId)} />
        ) : null}
      </div>

      <h2 className="text-base font-semibold text-slate-900">Qué llevás</h2>
      <ul className="flex flex-col divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
        {note.items.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-3 p-3">
            <span className="text-slate-900">{item.menuNameSnapshot}</span>
            <span className="font-semibold text-slate-900">{item.orderedQuantity}</span>
          </li>
        ))}
      </ul>

      {SIGNABLE_STATUSES.includes(note.status) ? (
        <Button className="h-14 w-full text-lg" disabled={queued !== undefined} onClick={() => setSigning(true)}>
          Firmar entrega
        </Button>
      ) : null}

      {signing ? (
        <SignOnScreenModal
          note={note}
          allowOffline
          onClose={() => setSigning(false)}
          onSigned={() => {
            setSigning(false);
            void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
            toast('success', 'Remito firmado');
          }}
          onQueued={(result) => {
            setSigning(false);
            void queue.refresh();
            toast(
              result.kind === 'queued' ? 'info' : 'error',
              result.kind === 'queued'
                ? 'Firma guardada en el celular. Se enviará cuando haya conexión.'
                : result.message,
            );
          }}
        />
      ) : null}
    </div>
  );
}
